import {
  MasterBarcodeEntry,
  MasterBarcodeStoreDetail,
  MasterCropData,
  MasterDesignData,
  MasterUPCData,
} from '../types';

export interface DesignBarcodeInfo {
  normalizedBarcodeValue?: string;
  normalizedBarcode?: string;
  priority: number;
  productLabel?: string;
}

export interface DerivedBarcodeRow {
  key: string;
  upc: string;
  normalizedBarcode: string;
  entry: MasterBarcodeEntry;
  matchedDesigns: MasterDesignData[];
  designCount: number;
  cropCount: number;
  isCasePack: boolean;
  isInactive: boolean;
  warningLabel?: string;
  warningMessage?: string;
}

export type BarcodeRowAction =
  | { kind: 'move-to-design'; designId: string; normalizedBarcode: string }
  | { kind: 'create-design'; normalizedBarcode: string }
  | { kind: 'blocked'; normalizedBarcode: string }
  | { kind: 'missing' };

function dedupeStoreDetails(details: MasterBarcodeStoreDetail[]): MasterBarcodeStoreDetail[] {
  const seen = new Set<string>();

  return details.filter((detail) => {
    const signature = JSON.stringify({
      storeLabel: detail.storeLabel,
      role: detail.role,
      name: detail.name || '',
      casePack: detail.casePack ?? null,
      images: detail.images.map((image) => ({
        url: image.url,
        sourceLabel: image.sourceLabel || '',
        type: image.type || '',
      })),
      isActive: detail.isActive ?? true,
    });

    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function resolvePrimaryUpcByStore(
  primaryEntries: MasterBarcodeEntry[],
  alternateEntries: MasterBarcodeEntry[]
): Map<string, string> {
  const preferredUpcByStore = new Map<string, string>();
  const orderedEntries = [...primaryEntries, ...alternateEntries];

  orderedEntries.forEach((entry) => {
    const normalizedUpc = entry.upc.trim();
    if (!normalizedUpc) return;

    entry.storeDetails.forEach((detail) => {
      if (detail.role !== 'primary') return;
      if (preferredUpcByStore.has(detail.storeLabel)) return;
      preferredUpcByStore.set(detail.storeLabel, normalizedUpc);
    });
  });

  return preferredUpcByStore;
}

function normalizeStoreRolesForEntry(
  entry: MasterBarcodeEntry,
  primaryUpcByStore: Map<string, string>
): MasterBarcodeEntry {
  const normalizedUpc = entry.upc.trim();

  return {
    ...entry,
    storeDetails: entry.storeDetails.map((detail) => {
      if (detail.role === 'case') {
        return detail;
      }

      return {
        ...detail,
        role:
          detail.role === 'primary' && primaryUpcByStore.get(detail.storeLabel) === normalizedUpc
            ? 'primary'
            : 'alternate',
      };
    }),
  };
}

function mergeBarcodeEntries(entries: MasterBarcodeEntry[]): MasterBarcodeEntry[] {
  const merged = new Map<string, MasterBarcodeEntry>();
  const orderedKeys: string[] = [];

  entries.forEach((entry, index) => {
    const normalized = entry.upc.trim();
    const mergeKey = normalized || `raw:${entry.upc}:${index}`;
    const existing = merged.get(mergeKey);

    if (!existing) {
      merged.set(mergeKey, {
        upc: entry.upc,
        provenance: [...entry.provenance],
        storeDetails: dedupeStoreDetails([...entry.storeDetails]),
      });
      orderedKeys.push(mergeKey);
      return;
    }

    const provenance = [...existing.provenance];
    entry.provenance.forEach((label) => {
      if (!provenance.includes(label)) provenance.push(label);
    });

    merged.set(mergeKey, {
      upc: existing.upc,
      provenance,
      storeDetails: dedupeStoreDetails([...existing.storeDetails, ...entry.storeDetails]),
    });
  });

  return orderedKeys
    .map((key) => merged.get(key))
    .filter((entry): entry is MasterBarcodeEntry => Boolean(entry));
}

export function getAllBarcodeEntries(upc: MasterUPCData): MasterBarcodeEntry[] {
  return [
    ...(upc.barcodeSummary?.primaryUpcs ?? []),
    ...(upc.barcodeSummary?.alternateUpcs ?? []),
    ...(upc.barcodeSummary?.casePacks ?? []),
  ];
}

export function buildBarcodeEntryLookup(
  entries: MasterBarcodeEntry[]
): Map<string, MasterBarcodeEntry> {
  return new Map(
    entries
      .map((entry) => [entry.upc.trim(), entry] as const)
      .filter(([normalized]) => Boolean(normalized))
      .filter(
        ([normalized], index, all) => all.findIndex(([value]) => value === normalized) === index
      )
  );
}

function canSeedDesignIdentity(crop: MasterCropData): boolean {
  return crop.source === 'scan' || crop.source === 'forge';
}

export function getFirstScanOrForgeBarcode(crops: MasterCropData[]): string | undefined {
  return [...crops]
    .filter((crop) => canSeedDesignIdentity(crop) && crop.normalizedBarcode)
    .sort((left, right) => left.id.localeCompare(right.id))[0]?.normalizedBarcode;
}

export function resolveDraftDesignVerifiedBarcode(
  crops: MasterCropData[],
  _fallbackBarcode?: string | null
): string | undefined {
  const barcodeCounts = new Map<string, { count: number; firstIndex: number }>();

  crops.forEach((crop, index) => {
    if (!canSeedDesignIdentity(crop)) return;
    const normalizedBarcode =
      typeof crop.normalizedBarcode === 'string' ? crop.normalizedBarcode.trim() : '';
    if (!normalizedBarcode) return;

    const existing = barcodeCounts.get(normalizedBarcode);
    if (existing) {
      existing.count += 1;
      return;
    }
    barcodeCounts.set(normalizedBarcode, { count: 1, firstIndex: index });
  });

  const winningEntry = [...barcodeCounts.entries()].sort((left, right) => {
    if (left[1].count !== right[1].count) return right[1].count - left[1].count;
    if (left[1].firstIndex !== right[1].firstIndex) return left[1].firstIndex - right[1].firstIndex;
    return left[0].localeCompare(right[0]);
  })[0];

  return winningEntry?.[0];
}

export function getDesignBarcodeInfo(
  design: MasterDesignData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): DesignBarcodeInfo {
  const representativeCrop =
    design.crops.find((crop) => crop.id === design.representativeCropId) || design.crops[0];
  const firstScanOrForgeBarcode = getFirstScanOrForgeBarcode(design.crops);
  const barcodeCandidates = [
    { value: design.normalizedBarcode, priority: 1 },
    {
      value:
        representativeCrop && canSeedDesignIdentity(representativeCrop)
          ? representativeCrop.normalizedBarcode
          : undefined,
      priority: 2,
    },
    { value: firstScanOrForgeBarcode, priority: 3 },
  ];
  const selected = barcodeCandidates.find((candidate) => candidate.value?.trim());
  const normalizedBarcode = selected?.value?.trim();
  const matchedEntry = normalizedBarcode
    ? barcodeEntryByNormalized.get(normalizedBarcode)
    : undefined;
  const displayDetail = matchedEntry?.storeDetails[0];

  return {
    normalizedBarcodeValue: matchedEntry?.upc ?? selected?.value,
    normalizedBarcode,
    priority: selected?.priority ?? 99,
    productLabel: displayDetail?.name
      ? `${displayDetail.name}${displayDetail.casePack ? ` (${displayDetail.casePack})` : ''}`
      : undefined,
  };
}

export function resolveDesignTargetBarcode(
  design: MasterDesignData,
  upc: MasterUPCData
): string | undefined {
  const lookup = buildBarcodeEntryLookup(getAllBarcodeEntries(upc));
  return getDesignBarcodeInfo(design, lookup).normalizedBarcodeValue;
}

export function compareDesignsByBarcode(
  left: MasterDesignData,
  right: MasterDesignData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): number {
  const leftInfo = getDesignBarcodeInfo(left, barcodeEntryByNormalized);
  const rightInfo = getDesignBarcodeInfo(right, barcodeEntryByNormalized);
  const leftHasBarcode = leftInfo.normalizedBarcode ? 0 : 1;
  const rightHasBarcode = rightInfo.normalizedBarcode ? 0 : 1;

  if (leftHasBarcode !== rightHasBarcode) return leftHasBarcode - rightHasBarcode;
  if (leftInfo.normalizedBarcode !== rightInfo.normalizedBarcode) {
    return (leftInfo.normalizedBarcode || '').localeCompare(rightInfo.normalizedBarcode || '');
  }
  if (leftInfo.priority !== rightInfo.priority) return leftInfo.priority - rightInfo.priority;

  return left.id.localeCompare(right.id);
}

export function getSortedDesigns(
  upc: MasterUPCData,
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>
): MasterDesignData[] {
  return [...upc.designs].sort((left, right) =>
    compareDesignsByBarcode(left, right, barcodeEntryByNormalized)
  );
}

export function buildDerivedBarcodeRows(upc: MasterUPCData): {
  barcodeRows: DerivedBarcodeRow[];
  barcodeEntryByNormalized: Map<string, MasterBarcodeEntry>;
} {
  const primaryEntries = upc.barcodeSummary?.primaryUpcs ?? [];
  const alternateEntries = upc.barcodeSummary?.alternateUpcs ?? [];
  const casePackEntries = upc.barcodeSummary?.casePacks ?? [];
  const primaryUpcByStore = resolvePrimaryUpcByStore(primaryEntries, alternateEntries);
  const mergedBarcodeEntries = mergeBarcodeEntries(
    [...primaryEntries, ...alternateEntries, ...casePackEntries].map((entry) =>
      normalizeStoreRolesForEntry(entry, primaryUpcByStore)
    )
  );
  const barcodeEntryByNormalized = buildBarcodeEntryLookup(mergedBarcodeEntries);
  const sortedDesigns = getSortedDesigns(upc, barcodeEntryByNormalized);

  const barcodeRows = mergedBarcodeEntries.map((entry, index) => {
    const normalizedBarcode = entry.upc.trim() || `row-${index}`;
    const matchedDesigns = sortedDesigns.filter(
      (design) =>
        getDesignBarcodeInfo(design, barcodeEntryByNormalized).normalizedBarcode ===
        normalizedBarcode
    );
    const cropCount = matchedDesigns.reduce((sum, design) => sum + design.crops.length, 0);
    const isCasePack = entry.storeDetails.some((detail) => detail.role === 'case');
    const isInactive =
      entry.storeDetails.length > 0 &&
      entry.storeDetails.every((detail) => detail.isActive === false);
    const shouldWarnForCasePack = isCasePack && cropCount > 0;
    const warningLabel =
      isInactive && isCasePack
        ? 'Inactive case pack'
        : shouldWarnForCasePack
          ? 'Case pack'
          : isInactive
            ? 'Inactive UPC'
            : undefined;
    const warningMessage =
      isInactive && isCasePack
        ? 'Inactive case pack: this case-pack barcode is no longer active for this item in SQL, but is not active for another item in this store.'
        : shouldWarnForCasePack
          ? 'Case-pack UPC: related designs are shown for review. Verify before grouping or moving crops.'
          : isInactive
            ? 'Inactive UPC: this barcode is no longer active for this item in SQL, but is not active for another item in this store.'
            : undefined;

    return {
      key: `barcode-${normalizedBarcode}-${index}`,
      upc: entry.upc,
      normalizedBarcode,
      entry,
      matchedDesigns,
      designCount: matchedDesigns.length,
      cropCount,
      isCasePack,
      isInactive,
      warningLabel,
      warningMessage,
    };
  });

  return {
    barcodeRows,
    barcodeEntryByNormalized,
  };
}

export function resolveBarcodeRowAction(
  upc: MasterUPCData,
  normalizedBarcode: string
): BarcodeRowAction {
  const row = buildDerivedBarcodeRows(upc).barcodeRows.find(
    (entry) => entry.upc === normalizedBarcode
  );

  if (!row) return { kind: 'missing' };
  if (row.designCount > 1) return { kind: 'blocked', normalizedBarcode: row.upc };
  if (row.designCount === 0) return { kind: 'create-design', normalizedBarcode: row.upc };

  return {
    kind: 'move-to-design',
    normalizedBarcode: row.upc,
    designId: row.matchedDesigns[0].id,
  };
}
